import { t } from "@lingui/macro";
import { ArrowRightIcon, FolderOpenIcon, ShieldCheckIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useRef, useState } from "react";

const MAX_BYTES = 10 * 1024 * 1024;

const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "image/webp",
];

const ACCEPTED_EXTENSION = /\.(pdf|docx|jpe?g|png|webp)$/i;

const IMPORT_ASSETS_PATH = "/images/user-space/onboarding/import-document";



type Props = {
  busy: boolean;
  onFile: (file: File) => void;
  onBack: () => void;
};

export const DocumentUploadStep = ({ busy, onFile, onBack }: Props) => {
  // ⚠️ Doit être DANS le composant pour que t`` soit évalué après le chargement du catalogue Lingui
  // Sinon les IDs bruts s'affichent (ex: AOeRfqVnbELL) à la place du texte
  const acceptedFormats = [
    {
      name: t`PDF (.pdf)`,
      image: `${IMPORT_ASSETS_PATH}/import-format-pdf.webp`,
    },
    {
      name: t`Word (.docx)`,
      image: `${IMPORT_ASSETS_PATH}/import-format-word.webp`,
    },
    {
      name: t`Image`,
      image: `${IMPORT_ASSETS_PATH}/import-format-image.webp`,
    },
  ];
  const inputRef = useRef<HTMLInputElement>(null);

  const [dragging, setDragging] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const selectFile = (file?: File) => {
    setError(null);

    if (!file) {
      return;
    }

    const hasAcceptedMimeType = ACCEPTED_MIME_TYPES.includes(file.type);

    const hasAcceptedExtension = ACCEPTED_EXTENSION.test(file.name);

    if (!hasAcceptedMimeType && !hasAcceptedExtension) {
      setError(
        t`Format non accepté. Choisissez un PDF (.pdf), un fichier Word (.docx) ou une image JPG (.jpg), PNG (.png) ou WebP (.webp).`,
      );

      return;
    }

    if (file.size > MAX_BYTES) {
      setError("Le fichier dépasse la taille maximale de 10 Mo.");

      return;
    }

    onFile(file);
  };

  const openFilePicker = () => {
    if (!busy) {
      inputRef.current?.click();
    }
  };

  return (
    <section
      className={cn(
        "mx-auto flex h-full min-h-0 w-full max-w-3xl",
        "flex-col justify-center overflow-hidden",
      )}
    >
      <header className="shrink-0">
        <h1
          className={cn(
            "text-2xl font-bold tracking-tight text-indigo-950",
            "sm:text-3xl lg:text-[34px] lg:leading-[1.15]",
          )}
        >
          Importez votre CV
        </h1>

        <p
          className={cn(
            "mt-1.5 max-w-2xl",
            "text-xs leading-5 text-indigo-950/75",
            "sm:text-sm sm:leading-6",
          )}
        >
          Nous récupérons automatiquement vos informations pour vous faire gagner du temps.
        </p>
      </header>

      <div
        role="button"
        tabIndex={busy ? -1 : 0}
        aria-disabled={busy}
        aria-label="Déposer ou sélectionner un CV à importer"
        onClick={openFilePicker}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openFilePicker();
          }
        }}
        onDragEnter={(event) => {
          event.preventDefault();

          if (!busy) {
            setDragging(true);
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();

          if (!busy) {
            setDragging(true);
          }
        }}
        onDragLeave={(event) => {
          event.preventDefault();

          if (event.currentTarget.contains(event.relatedTarget as Node)) {
            return;
          }

          setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);

          if (busy) {
            return;
          }

          selectFile(event.dataTransfer.files?.[0]);
        }}
        className={cn(
          "mt-4 flex min-h-[220px] shrink flex-col",
          "items-center justify-center overflow-hidden",
          "rounded-2xl border-2 border-dashed",
          "px-4 py-4 text-center",
          "transition-colors duration-200",
          "focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
          "sm:mt-5 sm:min-h-[245px] sm:px-6 sm:py-5",
          "lg:min-h-[255px]",
          busy ? "cursor-wait opacity-70" : "cursor-pointer",
          dragging
            ? "border-emerald-400 bg-emerald-50"
            : "border-indigo-300 bg-gradient-to-b from-violet-50/60 to-white hover:border-indigo-500 hover:bg-indigo-50/50",
        )}
      >
        <div
          aria-label={t`Formats acceptés : PDF (.pdf), Word (.docx) et image (.jpg, .jpeg, .png, .webp)`}
          className={cn("flex shrink-0 items-end justify-center", "gap-2.5 sm:gap-4")}
        >
          {acceptedFormats.map((format) => (
            <figure key={format.name} className="flex flex-col items-center">
              <img
                src={format.image}
                alt=""
                aria-hidden
                draggable={false}
                className={cn(
                  "size-14 object-contain",
                  "drop-shadow-[0_5px_9px_rgba(15,23,42,0.12)]",
                  "sm:size-[66px] lg:size-[70px]",
                )}
              />

              <figcaption
                className={cn("mt-1 text-[9px] font-semibold", "text-indigo-950/60 sm:text-[10px]")}
              >
                {format.name}
              </figcaption>
            </figure>
          ))}
        </div>

        <p className={cn("mt-2.5 text-base font-bold text-indigo-950", "sm:mt-3 sm:text-lg")}>
          Glissez votre fichier ici
        </p>

        <p
          className={cn("mt-0.5 text-[11px] text-indigo-950/65", "sm:text-xs")}
        >{t`PDF (.pdf), Word (.docx) ou image (.jpg, .jpeg, .png, .webp) · 10 Mo maximum`}</p>

        <button
          type="button"
          disabled={busy}
          onClick={(event) => {
            event.stopPropagation();
            openFilePicker();
          }}
          className={cn(
            "mt-3 inline-flex min-h-10 items-center justify-center gap-2",
            "rounded-xl border-2 border-indigo-600 bg-white",
            "px-5 text-xs font-bold text-indigo-950",
            "shadow-[0_4px_12px_rgba(79,70,229,0.08)]",
            "transition-all duration-200",
            "hover:-translate-y-0.5 hover:bg-indigo-50",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "sm:min-h-11 sm:px-6 sm:text-sm",
          )}
        >
          <FolderOpenIcon aria-hidden size={18} weight="duotone" className="shrink-0" />

          {busy ? "Importation…" : "Choisir un fichier"}
        </button>

        <input
          ref={inputRef}
          type="file"
          accept={[
            ".pdf",
            ".docx",
            ".jpg",
            ".jpeg",
            ".png",
            ".webp",
            "application/pdf",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "image/jpeg",
            "image/png",
            "image/webp",
          ].join(",")}
          className="sr-only"
          onChange={(event) => {
            selectFile(event.target.files?.[0]);

            event.target.value = "";
          }}
        />
      </div>

      {error && (
        <p
          role="alert"
          className={cn(
            "mt-2.5 shrink-0 rounded-xl",
            "border border-rose-200 bg-rose-50",
            "px-3 py-2 text-[11px] font-medium",
            "leading-4 text-rose-700 sm:text-xs",
          )}
        >
          {error}
        </p>
      )}

      <p
        className={cn(
          "mt-2.5 flex shrink-0 items-center justify-center gap-2",
          "text-center text-[10px] font-medium text-indigo-950/70",
          "sm:mt-3 sm:text-xs",
        )}
      >
        <ShieldCheckIcon
          aria-hidden
          size={18}
          weight="duotone"
          className="shrink-0 text-emerald-500 sm:size-5"
        />
        Votre document reste confidentiel et sécurisé.
      </p>

      <footer className={cn("mt-3 flex shrink-0 items-center justify-between gap-3", "sm:mt-4")}>
        <button
          type="button"
          disabled={busy}
          onClick={onBack}
          className={cn(
            "min-h-10 rounded-xl border-2 border-indigo-600",
            "px-5 text-xs font-bold text-indigo-700",
            "transition-colors hover:bg-indigo-50",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "sm:min-h-11 sm:px-6 sm:text-sm",
          )}
        >
          Retour
        </button>

        <button
          type="button"
          disabled
          className={cn(
            "inline-flex min-h-10 cursor-not-allowed",
            "items-center justify-center gap-2 rounded-xl",
            "bg-slate-100 px-5",
            "text-xs font-bold text-slate-400",
            "sm:min-h-11 sm:px-7 sm:text-sm",
          )}
        >
          Continuer
          <ArrowRightIcon aria-hidden size={17} weight="bold" className="shrink-0" />
        </button>
      </footer>
    </section>
  );
};
